'use client';

import { useEffect, useState } from 'react';
import { useDeviceSelectors } from 'react-device-detect';
import config, { CHANNEL_PARAMETER } from 'siteconfig';
import {
  POSTHOG_SIGNUP_EVENT_IMESSAGE,
  POSTHOG_SIGNUP_EVENT_SMS,
  POSTHOG_SIGNUP_EVENT_WEB_CHAT,
} from 'utilities/analytics';

/**
 * Channels we will route a visitor back to. Deliberately not every channel in
 * CHANNEL_PRIORITY_ORDER: only these carry the keyword as message text.
 * Telegram `start` payloads reject the space in `Sign ABCDEF`, and Meta
 * deprecated Messenger's `?text=`, so a visitor sent there lands in an empty
 * thread with no idea what to type — worse than a login (BOT-843). Anyone on
 * one of those goes to web chat instead, which does carry the keyword.
 */
const ROUTABLE_CHANNELS = {
  apple: {
    url: config.channelUrls.apple,
    eventName: POSTHOG_SIGNUP_EVENT_IMESSAGE,
  },
  sms: {
    url: config.channelUrls.sms,
    eventName: POSTHOG_SIGNUP_EVENT_SMS,
  },
} as const;

type RoutableChannel = keyof typeof ROUTABLE_CHANNELS;

const asRoutableChannel = (value: string | null): RoutableChannel | null =>
  value === 'apple' || value === 'sms' ? value : null;

/**
 * Resolves which channel a "start a conversation" control should open.
 *
 * Two inputs, in priority order:
 *
 * 1. The `channel` parameter the bot appends to links it sends. A visitor
 *    arriving from a conversation is already a contact we can reach, so we
 *    return them to that thread rather than asking them to log in — and we
 *    avoid minting a second contact on a channel they weren't using.
 * 2. Failing that, the device. Apple hardware deep links into Apple Messages,
 *    which skips the email/PIN wall that costs us conversions on iOS.
 *
 * Web chat is the floor under both: a `channel` we cannot route or the device
 * cannot open falls there rather than to device detection, because it still
 * carries the keyword. So a deep link is only ever handed to a device that can
 * open it, and the parameter can never talk us into one that cannot.
 *
 * All three pieces of state start unset so the server render and the first
 * client render agree.
 */
export const useChatChannel = (keyword: string) => {
  const [supportsApple, setSupportsApple] = useState(false);
  const [supportsSms, setSupportsSms] = useState(false);
  const [channelParam, setChannelParam] = useState<string | null>(null);
  const [selectors] = useDeviceSelectors(
    typeof window !== 'undefined' ? window?.navigator?.userAgent : 'server'
  );

  useEffect(() => {
    const apple = selectors.isIOS || selectors.isMacOs;
    setSupportsApple(apple);
    // macOS hands `sms:` to Messages, so Apple desktops count. A Windows or
    // Linux desktop has nothing registered for the scheme and the link dies.
    setSupportsSms(apple || Boolean(selectors.isMobile));
  }, [selectors]);

  useEffect(() => {
    setChannelParam(
      new URLSearchParams(window.location.search)
        .get(CHANNEL_PARAMETER)
        ?.toLowerCase() ?? null
    );
  }, []);

  const canOpen = (candidate: RoutableChannel) =>
    candidate === 'apple' ? supportsApple : supportsSms;

  const requested = asRoutableChannel(channelParam);
  const fromParam = channelParam
    ? requested && canOpen(requested)
      ? requested
      : 'webchat'
    : null;

  const channel: RoutableChannel | 'webchat' =
    fromParam ?? (supportsApple ? 'apple' : 'webchat');
  const routed = channel === 'webchat' ? null : ROUTABLE_CHANNELS[channel];
  const webChatHref = `/chat?trigger=${encodeURIComponent(keyword)}`;

  return {
    channel,
    /** True when the primary control leaves the site for a messaging app. */
    isDeepLink: routed !== null,
    /** Drives Apple-specific copy and iconography, not the destination. */
    supportsAppleMessages: channel === 'apple',
    webChatHref,
    href: routed ? routed.url + keyword : webChatHref,
    eventName: routed ? routed.eventName : POSTHOG_SIGNUP_EVENT_WEB_CHAT,
  };
};
